// §3 — Contract execution: the explicit "contract signed" step between the
// accepted contract review and the job-number request. Gate rules live in
// contractIssues.js and are enforced again by sp_MarkContractExecuted.
import { useState } from "react";
import { contractGate, EXECUTED_CONTRACT_DOC_TYPE, exceptionRecorded, exceptionOutstanding, exceptionDateText } from "./contractIssues";

const input = {
  padding: "7px 10px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 6,
  background: "var(--surface)", color: "var(--text)", boxSizing: "border-box", fontFamily: "inherit", width: "100%",
};
const label = { fontSize: 11, fontWeight: 600, color: "var(--text3)", display: "block", marginBottom: 4 };

export default function ContractExecutionPanel({ reviews, issues, turnover, documents, section3, canAct, busy, onExecute, onClear, onGoToReview, onSetDocType = null, isAdmin = false, onRequestException = null }) {
  const gate = contractGate({ reviews, issues, turnover, documents, section3 });
  // §3 uploads that aren't typed Executed Contract (an untyped upload, or one typed
  // Purchase Order) — the gate ignores them, so offer to re-type the right one.
  const otherDocs = (documents || []).filter(d =>
    (!section3 || d.SectionID === section3.SectionID) && d.DocType !== EXECUTED_CONTRACT_DOC_TYPE);
  const [docId, setDocId] = useState("");
  const [date, setDate] = useState("");
  const [signedBy, setSignedBy] = useState("");
  const [exReason, setExReason] = useState("");
  const [exDate, setExDate] = useState("");
  const inReview = turnover?.Status === "Contract Review";
  // Administrator-only way to request the job number before the contract is signed.
  const canRequestException = isAdmin && !!onRequestException && inReview && gate.readyToExecute && !exceptionRecorded(turnover);
  const exReady = exReason.trim().length > 0 && !!exDate;

  if (gate.executed) {
    const doc = (documents || []).find(d => d.TurnoverDocumentID === turnover.ContractExecutedDocumentID);
    return (
      <div style={{ marginBottom: 20, padding: "12px 16px", border: "1px solid #bbf7d0", background: "#f0fdf4", borderRadius: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#166534" }}>✓ Contract executed</div>
        <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 4 }}>
          Signed {turnover.ContractExecutedDate ? new Date(String(turnover.ContractExecutedDate).slice(0, 10) + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—"}
          {" "}by {turnover.ContractSignedBy || "—"} for CleanSpace
          {doc && <> · <a href={doc.SharePointUrl} target="_blank" rel="noreferrer">{doc.FileName || "executed contract"}</a></>}
          {turnover.ContractExecutedRecordedBy && <span style={{ color: "var(--text3)" }}> · recorded by {turnover.ContractExecutedRecordedBy}</span>}
        </div>
        {exceptionRecorded(turnover) && (
          <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 4 }}>
            The job number was requested before signing (approved by {turnover.JobNumberExceptionBy || "—"}) — the executed contract has now been recorded.
          </div>
        )}
        {inReview && canAct && (
          <button onClick={onClear} disabled={busy} style={{ marginTop: 8, padding: "4px 12px", fontSize: 12, background: "none", border: "1px solid var(--cs-border)", borderRadius: 6, cursor: "pointer", color: "var(--text2)" }}>
            Undo (before submitting for the job number)
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 20, border: `1px solid ${gate.readyToExecute ? "#bae6fd" : "#fcd34d"}`, background: gate.readyToExecute ? "#f0f9ff" : "#fffbeb", borderRadius: 8, padding: "14px 16px" }}>
      {exceptionOutstanding(turnover) && (
        <div data-testid="exception-banner" style={{ marginBottom: 12, padding: "10px 14px", background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: 6, fontSize: 12, color: "#92400e" }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Job number requested without an executed contract — documents to follow</div>
          <div><strong>Why:</strong> {turnover.JobNumberExceptionReason}</div>
          <div><strong>Expected:</strong> {exceptionDateText(turnover.JobNumberExceptionExpectedDate)} · <strong>Approved by:</strong> {turnover.JobNumberExceptionBy || "—"}</div>
          <div style={{ marginTop: 4 }}>Upload the signed contract (Type: Executed Contract) and record it below as soon as it arrives. Section 3 stays open until then.</div>
        </div>
      )}
      <div style={{ fontSize: 13, fontWeight: 700, color: gate.readyToExecute ? "#075985" : "#92400e" }}>
        {gate.readyToExecute ? "Ready to sign — record the executed contract" : "Not ready to sign yet"}
      </div>
      {!gate.readyToExecute ? (
        <>
          <div style={{ fontSize: 12, color: "var(--text2)", margin: "6px 0" }}>The contract can be signed once every department has signed off and every must-resolve issue is accepted. Still outstanding:</div>
          <ul style={{ margin: "0 0 8px", paddingLeft: 18, fontSize: 12, color: "#92400e" }}>
            {gate.blockers.slice(0, 8).map((b, i) => <li key={i}>{b.text}</li>)}
            {gate.blockers.length > 8 && <li>…and {gate.blockers.length - 8} more</li>}
          </ul>
          <button onClick={onGoToReview} style={{ padding: "5px 12px", fontSize: 12, fontWeight: 700, background: "none", border: "1px solid var(--cs-border)", borderRadius: 6, cursor: "pointer", color: "var(--text2)" }}>Go to Contract Review →</button>
        </>
      ) : !canAct ? (
        <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 6 }}>Waiting on {section3?.AssignedTo || "Sales"} to record the executed contract.</div>
      ) : gate.executedDocs.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 6 }}>
          Upload the signed contract below with Type <strong>Executed Contract</strong>, then record it here.
          {otherDocs.length > 0 && onSetDocType && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #bae6fd" }}>
              <div style={{ fontWeight: 600, color: "var(--text)", marginBottom: 6 }}>Already uploaded to this section? Pick the signed contract:</div>
              {otherDocs.map(d => (
                <div key={d.TurnoverDocumentID} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600 }}>{d.FileName || d.Description || `Document ${d.TurnoverDocumentID}`}</span>
                  <span style={{ color: "var(--text3)" }}>{d.DocType ? `typed ${d.DocType}` : "no type"}</span>
                  <button
                    disabled={busy}
                    onClick={() => onSetDocType(d, EXECUTED_CONTRACT_DOC_TYPE)}
                    style={{ padding: "3px 10px", fontSize: 12, fontWeight: 700, background: "var(--cs-blue)", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", opacity: busy ? 0.5 : 1 }}
                  >
                    This is the executed contract
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1.5fr auto", gap: 10, alignItems: "end", marginTop: 10 }}>
          <div>
            <label style={label}>Executed contract</label>
            <select value={docId} onChange={e => setDocId(e.target.value)} style={input}>
              <option value="">Choose the uploaded document…</option>
              {gate.executedDocs.map(d => <option key={d.TurnoverDocumentID} value={d.TurnoverDocumentID}>{d.FileName || d.Description || `Document ${d.TurnoverDocumentID}`}</option>)}
            </select>
          </div>
          <div>
            <label style={label}>Date signed</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} style={input} />
          </div>
          <div>
            <label style={label}>Signed for CleanSpace by</label>
            <input value={signedBy} onChange={e => setSignedBy(e.target.value)} placeholder="Name and title" style={input} />
          </div>
          <button
            disabled={busy || !docId || !date || !signedBy.trim()}
            onClick={() => onExecute({ documentId: Number(docId), executedDate: date, signedBy: signedBy.trim() })}
            style={{ padding: "8px 16px", fontSize: 13, fontWeight: 700, background: "var(--cs-blue)", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap", opacity: busy || !docId || !date || !signedBy.trim() ? 0.5 : 1 }}
          >
            Mark contract executed
          </button>
        </div>
      )}

      {canRequestException && (
        <details style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #bae6fd" }}>
          <summary style={{ fontSize: 12, fontWeight: 700, color: "var(--text2)", cursor: "pointer" }}>
            No executed contract yet? Request the job number anyway (administrator)
          </summary>
          <div style={{ fontSize: 12, color: "var(--text2)", margin: "8px 0" }}>
            Skips only the executed-contract step. The job number goes to Accounting flagged “contract to follow”, with your reason and the date below, and Section 3 stays open until the signed contract is recorded.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: 10, alignItems: "end" }}>
            <div>
              <label htmlFor="exception-reason" style={label}>Why is the job number needed now?</label>
              <input id="exception-reason" value={exReason} onChange={e => setExReason(e.target.value)} maxLength={1000} placeholder="e.g. Letter of intent received; contract being finalised" style={input} />
            </div>
            <div>
              <label htmlFor="exception-date" style={label}>Executed contract expected by</label>
              <input id="exception-date" type="date" value={exDate} onChange={e => setExDate(e.target.value)} style={input} />
            </div>
            <button
              disabled={busy || !exReady}
              onClick={() => onRequestException({ reason: exReason.trim(), expectedDate: exDate })}
              style={{ padding: "8px 16px", fontSize: 13, fontWeight: 700, background: "#b45309", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap", opacity: busy || !exReady ? 0.5 : 1 }}
            >
              Request job number — contract to follow
            </button>
          </div>
        </details>
      )}
    </div>
  );
}
